import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Collapse, Stack, Tooltip, Typography, Skeleton, useTheme } from '@mui/material'

import {
  METRIC_BY_ID,
  METRIC_INPUT_LABELS,
  type IRoiWithheldMetric,
  type VeraSpendSource,
} from '@repo/types'
import { Card } from '@repo/ui'

import {
  useAnalyticsRoiHero,
  useCurrentUserCompany,
  useIntroData,
  useMetricInputs,
} from 'hooks/api'
import { useSelector } from 'store/hooks'

import { AnalyticsBlock } from 'components/display/Analytics/Analytics'
import { DisclosureTextButton } from 'components/input'

import { formatMoneyCentsCompact } from './analyticsMoney'
import { ROI_VALUE_CHART_SCOPE, RoiValueChart } from './RoiValueChart'
import {
  formatRoiHeroRange,
  formatRoiHeroScope,
  useRoiHeroAllTimeRange,
} from './RoiHeroPeriod'

/**
 * The ROI hero: value delivered, what you paid Vera, and ONE multiple.
 *
 * The multiple lives here and nowhere else, deliberately. Dividing each card by the
 * same denominator would produce per-card multiples that are not additive, and a
 * reader shown six of them will add them. One numerator sum, one division, one
 * multiple — see `IAnalyticsMetric.contributesToRoi`.
 *
 * ## ⚠️ THIS CARD DOES NOT FOLLOW THE PAGE'S DATE RANGE, AND IT NO LONGER OFFERS A CHOICE
 *
 * Its scope is FIXED AT ALL TIME and STATED in its own copy — the `All time · since <date>`
 * line under the figures. There is no `Period:` control and no `Sync with analytics`; both
 * were removed, and `RoiHeroPeriod` carries the argument for why every option they offered
 * made the figure worse-founded than the default they sat beside.
 *
 * The reason the card is decoupled at all is the DENOMINATOR, and it is not a preference.
 * Spend is settled Stripe invoices — money that landed in lumps on invoice dates — so "the
 * last 30 days" is a perfectly good question to ask of a scan count and a poor one to ask of
 * a bill, because a 30-day window that happens to fall between two invoices reports a
 * denominator of zero. One control could not serve both, so the page keeps the measurement
 * window and the hero takes the window the ratio is actually meaningful over: all of it.
 *
 * ⚠️ FIXED SCOPE DID NOT MERGE THE SLOTS. Do not "simplify" this card into reading the
 * page's `analytics.roi` response. `GET_ANALYTICS_ROI` and `GET_ANALYTICS_ROI_HERO` are
 * distinct `takeLatest` sagas writing distinct slots, and collapsing them makes a filter
 * change a race whose loser silently paints a multiple measured over a window this card is
 * not displaying. The card still issues its own all-time request; only the user-facing
 * control went.
 *
 * ⚠️ AND IT IS HALF OF A PAIR. Removing this control is only honest because the page's date
 * picker moved DOWN into the toolbar row, below this card — see
 * `AnalyticsFiltersHeader.dateRangeControl`. A page-level date control sitting ABOVE a card
 * it does not govern is a scope lie; putting it beneath the hero is what makes "everything
 * beneath me" true. Neither half survives alone.
 *
 * Two consequences worth knowing:
 *
 *  1. The request goes to a SECOND redux slot (`analytics.roiHero`). Two differently
 *     scoped fetches sharing one slot is a last-writer-wins race — see
 *     `Actions.GET_ANALYTICS_ROI_HERO`.
 *  2. The page's DIMENSION filters (owner / product / certificate / location) are still
 *     applied, exactly as before. Only the DATE is decoupled, because only the date had a
 *     conflict. ⚠️ Note that a dimension filter narrows the NUMERATOR while the
 *     denominator stays company-wide — filtering to one product reports that product's
 *     value over the whole company's bill. That is pre-existing behaviour, unchanged
 *     here; it is recorded rather than silently carried forward.
 *
 * ## Why this reuses `AnalyticsBlock` rather than styling three figures itself
 *
 * The hero used to hand-roll its own label + figure typography (`body2` over `h2`), which
 * was a second stat treatment on a page that already has one, and it drifted: the KPI tiles
 * render at 32px semibold inside a `Card`, the hero at `h2` inside a bare bordered `Box`. So
 * the three figures now ARE `AnalyticsBlock`s — same title row, same figure, same info
 * tooltip, same warning-toned note — passed `surface="bare"` so they sit inside this one
 * card instead of drawing three of their own (§36/§47).
 *
 * ONE card, not three side-by-side tiles, because the footnotes below qualify all three
 * figures JOINTLY. "Counts N measured sources" is a statement about the numerator, which is
 * only visible in Value delivered but constrains the multiple too; the approximation caveat
 * is about the denominator, which is Spend but again constrains the multiple. Attached to
 * any single tile, each would be read as being about that tile alone. So the surface that
 * carries them has to be the one that contains all three.
 *
 * It is WIDER (it spans the row) and its figures are larger than the tiles' 32px, which is
 * the page's reading order made visual: the headline sits above the detail rather than
 * beside it.
 *
 * ## Why the MULTIPLE leads, and is bigger than its own inputs
 *
 * The original order was value ÷ spend = return, which is the arithmetic in reading order
 * and is genuinely the clearer way to present an EQUATION. A hero is not an equation. It is
 * read first-and-biggest-then-stop, so the figure that lands first has to be the answer, not
 * the first operand — a reader who stops after one number should leave with "Vera returned
 * 3.2×", not with a gross value they have no denominator for.
 *
 * The derivation is not thrown away, it is demoted: Value delivered and Spend stay ADJACENT
 * to each other, in their original order, on the far side of a rule — so the pair still reads
 * as "and here is what produced it". Separating them (one either side of the multiple) would
 * have kept all three visible while making the division unrecoverable.
 *
 * The size split does the same work typographically: `HERO_MULTIPLE_STAT_SIZE` (56) against
 * `HERO_INPUT_STAT_SIZE` (36) is a 1.55× step, large enough to be pre-attentive rather than
 * a subtle difference a reader has to notice. Both come from `AnalyticsBlock`'s existing
 * `statSize` prop — the hero does not hand-roll typography to get a hierarchy (§47).
 */

// `fmtCurrency` used to hardcode 'USD' — moved to `analyticsMoney.ts` (VERA-260), which
// threads the company's `reporting_currency` through instead (a fact about the figures,
// not a display preference). See that module's docblock.

/**
 * The headline figure — the ROI multiple. Well above `AnalyticsBlock`'s 32px default and
 * above its own two inputs, because it is the one number the page exists to report.
 */
const HERO_MULTIPLE_STAT_SIZE = 56

/**
 * Value delivered and Spend. Still above the 32px KPI tiles below the hero (these are hero
 * figures, not tiles) but clearly subordinate to the multiple they produce.
 */
const HERO_INPUT_STAT_SIZE = 36

/** "a", "a and b", "a, b and c" — an Oxford-comma-free list, which is the house voice. */
const joinList = (parts: string[]): string =>
  parts.length <= 1
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

/** A metric's own title, falling back to its id if the registry does not know it. */
const metricTitle = (id: string): string => METRIC_BY_ID[id]?.title ?? id

/**
 * ── WHY "N OF M", AND WHAT WOULD MAKE IT M OF M ────────────────────────────────────
 *
 * The tooltip on the `Partial · N of M sources` line, composed from the response rather
 * than written down.
 *
 * ⚠️ IT REPLACES A SENTENCE THAT ONLY EVER DESCRIBED WHAT *IS* COUNTED. The old copy
 * ("sums only the N of M sources Vera can measure today ... the other K are not computable
 * yet") restated the visible line and then stopped, so a reader who wanted to know WHICH
 * sources were missing, or whether anything they could do would recover them, learned
 * nothing from opening it. `IRoiSummary.withheld` carries exactly that — a reason and the
 * specific `missing_inputs` per metric — and it was going unread.
 *
 * ⚠️ THE TWO REASONS ARE REPORTED SEPARATELY, and conflating them misleads in opposite
 * directions (see `RoiWithheldReason`). `needs_input` is one figure away and the figure is
 * the brand's own; `not_computed` is Vera's gap and no input the brand supplies will move
 * it. Telling someone to go and set a number that will not change the result is the
 * specific outcome that makes people stop trusting a dashboard.
 *
 * ⚠️ IT NAMES A DESTINATION, AND CHECK THE DESTINATION STILL EXISTS BEFORE EDITING THIS.
 * An earlier draft of this copy ended "Vera cannot collect those in the app yet", which was
 * true of `main` and false of this branch: VRG-459 landed `Settings → Business figures`
 * (`SETTINGS_COMPANY_BUSINESS_FIGURES`), so the inputs ARE enterable. Shipping the earlier
 * sentence would have told a brand its headline figure was understated and that nothing
 * could be done, one click away from the form that fixes it.
 *
 * The reverse failure is the one the copy before THAT made: it attributed the shortfall to
 * "inputs you supply" when there was nowhere to supply them, blaming the reader for a gap
 * that was ours. Both failures come from asserting the destination's state instead of
 * checking it, which is why this note exists rather than just the string.
 *
 * WORDS, NOT A LINK. The route is named in prose rather than rendered as an anchor: this is
 * a hover tooltip, and a control that must be travelled to before it can be clicked is a
 * control a fair number of readers will lose on the way. Naming the settings row costs one
 * navigation and cannot mis-fire. A real deep link belongs on the inline surface if one is
 * ever added, not inside the hover.
 */
const sourcesExplanation = (
  scope: string,
  withheld: IRoiWithheldMetric[],
): string => {
  const needsInput = withheld.filter(w => w.reason === 'needs_input')
  const notComputed = withheld.filter(w => w.reason === 'not_computed')

  const sentences = [scope]

  if (needsInput.length > 0) {
    // De-duplicated: two metrics commonly want the same figure (gross margin drives more
    // than one), and listing it twice reads as two different things to go and find.
    //
    // The labels are used VERBATIM from `METRIC_INPUT_LABELS` — not lower-cased to sit
    // more naturally mid-sentence. They are the same strings the (future) inputs form
    // will show, and a reader matching "Gross margin" against a field called "gross
    // margin" is the small friction §44 exists to remove.
    const inputs = Array.from(
      new Set(needsInput.flatMap(w => w.missing_inputs.map(key => METRIC_INPUT_LABELS[key] ?? key))),
    )
    // AGREES WITH ITS OWN COUNT, and the single-metric case is now the COMMON one.
    // The sentence was written when this list carried all three input-driven metrics, so
    // it hardcoded "each needs … Set THEM". With the server no longer reporting the two
    // resolver-less metrics as `needs_input` (VERA-260), the usual reading is one metric
    // and one field — where "each needs a figure only you have: Annual return-fraud loss.
    // Set them in Settings" is three grammatical errors in one breath, on the one line
    // whose whole job is to be believed.
    const one = needsInput.length === 1
    const oneInput = inputs.length === 1
    sentences.push(
      `${joinList(needsInput.map(w => metricTitle(w.metric_id)))} would also count, but ${
        one ? 'it needs' : 'each needs'
      } a figure only you have: ${joinList(inputs)}. Set ${
        oneInput ? 'it' : 'them'
      } in Settings, under Business figures.`,
    )
  }

  if (notComputed.length > 0) {
    sentences.push(
      `${joinList(notComputed.map(w => metricTitle(w.metric_id)))} ${notComputed.length === 1 ? 'is' : 'are'} not something Vera can measure yet, so no figure you supply would change ${notComputed.length === 1 ? 'it' : 'them'}.`,
    )
  }

  sentences.push('So the value and the return above are understated rather than final.')

  return sentences.join(' ')
}

/**
 * One line, always, with the full sentence on hover.
 *
 * Shared by the hero caveats because they are the same object: a short visible signal that
 * the figure above is qualified, plus the explanation behind it. See the caveat block below
 * for why they are shortened rather than deleted, and why the signal stays VISIBLE.
 *
 * Matches `AnalyticsBlock`'s `note` treatment exactly — clamp, `cursor: help`, Tooltip
 * wrapper — so a caveat reads identically whether it sits on a tile or on the hero.
 */
const CAVEAT_SX = {
  lineHeight: 1.5,
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'help',
} as const

/**
 * ── WHY THERE IS NO MULTIPLE, BY CAUSE ─────────────────────────────────────────────
 *
 * Shown under the tiles whenever `multiple` is null AND spend is zero. It is the sentence
 * that explains the em-dash where a return figure should be, so it has to be TRUE, and
 * "true" differs by member of `IVeraSpend['source']`.
 *
 * 🚨 IT USED TO BLAME THE PERIOD, UNCONDITIONALLY: *"No Vera spend recorded for this
 * period, so there is nothing to divide by yet."* That sentence is right for exactly one
 * of the four sources and wrong for the rest, and its two wrong readings are the two that
 * cost something:
 *
 *  - `no_billing_account` — the brand is in trial, comped, or was never provisioned, so
 *    there is no Stripe customer AT ALL. Nothing about the selected window is responsible,
 *    and the old copy's "for this period" sent the reader to the period select, where
 *    widening the range cannot possibly help. "yet" also implied waiting would fix it.
 *  - `unavailable` — Stripe could not be reached, so spend is NOT KNOWN. The API reports
 *    zero deliberately rather than falling back to a plan projection (see `unavailable()`
 *    in `vera-spend-invoice.source.ts`: visibly missing beats quietly wrong), and an
 *    unknown denominator rendered as "no spend recorded" converts that honest zero back
 *    into a claim about the brand's spending. This is the same defect class this card has
 *    now corrected twice: growth-from-zero rendered as −100%, and a metric claiming an
 *    input would unblock it when no input could.
 *
 * ⚠️ `Record<VeraSpendSource, string>`, NOT a `switch` with a default. A new member of the
 * union then fails the BUILD rather than silently inheriting a sentence written for a
 * different cause — which is precisely how the single unconditional sentence survived the
 * arrival of three sources that contradict it.
 *
 * The chart's legend carries a parallel map (`ROI_UNAVAILABLE_REASON`) keyed on the same
 * union. They are deliberately NOT one string: this one explains a missing FIGURE and that
 * one explains an undrawable LINE, and collapsing them would force one register to serve
 * both. What is shared is the cause, and the type is what keeps the two in step.
 *
 * One sentence each, no em-dashes (§15).
 */
const NO_SPEND_EXPLANATION: Record<VeraSpendSource, string> = {
  /** Invoices WERE read. None settled inside the window, so the period really is the reason. */
  stripe_invoices: 'No Vera spend settled in this period, so there is nothing to divide by yet.',
  /** Not about the window. Says so plainly, and does not imply that waiting will change it. */
  no_billing_account:
    'This company does not have a Vera billing account, so there is no spend to divide by.',
  /** UNKNOWN, not zero. Must never read as a measured figure. */
  unavailable:
    'Vera could not load billing history just now, so return cannot be calculated. This is not a reading of zero spend.',
  /**
   * Not served to clients today (`getRoiMetrics` always replaces a projection with the
   * invoice-derived figure), but the union permits it, so it carries a true sentence rather
   * than an assumption that it cannot arrive.
   */
  plan_projection:
    'Vera spend for this period is estimated rather than billed, so no return is calculated from it.',
}

export const RoiHero = () => {
  const theme = useTheme()

  const filters = useSelector(state => state.analytics.filters)

  /**
   * ✅ THE PAGE'S ROI RESPONSE IS NO LONGER READ HERE AT ALL, and that is worth recording
   * because this card used to be SERIALISED BEHIND IT.
   *
   * It selected `state.analytics.roi` for one thing only: the Stripe billing boundaries
   * (`current_period_start` / `current_period_end`) that the `This billing period` menu row
   * was built from. That option is gone with the picker (`RoiHeroPeriod`), so the last
   * reason to depend on the page's slowest request is gone with it — this card now needs
   * nothing but the company's `created_at`.
   *
   * ⚠️ Do NOT reintroduce a read of `state.analytics.roi` here as a shortcut to the figures.
   * It is keyed to the PAGE's date range; this card displays an all-time window. Reading it
   * would render a multiple measured over a window the card is not claiming.
   */

  /**
   * ── WHERE "ALL TIME" STARTS: THE COMPANY'S OWN CREATION DATE ──────────────────────
   *
   * The period is FIXED at all time (see `RoiHeroPeriod`), so this card has to answer
   * "since when?" with something defensible — and now has to STATE it on screen, which
   * raises the bar rather than lowering it: the floor is no longer an implementation
   * detail behind a menu, it is the card's own printed claim. Three candidates existed;
   * this is why it is creation.
   *
   *  - ❌ `VERA_DATA_FLOOR` (2020-01-01) — the FABRICATED floor the old projection used,
   *    and the one the previous seam recipe in `RoiHeroPeriod` would have reinstated. It
   *    no longer corrupts the denominator (there are no invoices in years the brand did
   *    not exist), but it asserts a history the brand does not have and pads the chart
   *    with ~80 empty months that flatten every real lump onto the baseline.
   *
   *  - ❌ THE FIRST PAID INVOICE — superficially the tightest bound, and wrong in the
   *    flattering-to-nobody direction. Value delivered before the first invoice (trial,
   *    onboarding, a comped first month) is REAL value that Vera returned; starting the
   *    window at the first payment drops it from the numerator while the denominator is
   *    unchanged — by definition there is no spend before the first invoice — so it
   *    UNDERSTATES the return. It is also not exposed: `IVeraSpend` carries
   *    `invoice_count`, not a first-payment date, so choosing it would mean widening the
   *    API response for a bound that is worse than the free one.
   *
   *  - ✅ `company.created_at` — the earliest instant this brand can have ANY Vera data,
   *    which is exactly what "all time" claims to cover. Both sides of the ratio are then
   *    complete over the same window: every invoice ever paid, against every value event
   *    ever recorded.
   *
   * This is the `dataFloor` the shared shortcut factory has always accepted and nothing has
   * ever passed — see `DateRangeShortcutOptions.dataFloor`.
   *
   * ── ⚠️ …EXCEPT WHEN THE TENANT IS HOLDING SAMPLE DATA (VRG-512 v10) ────────────────
   *
   * "The earliest instant this brand can have any Vera data" is a true statement about a
   * REAL brand and a false one about a seeded one. The intro sample set fabricates ~11
   * months of history — products, certificates, 22 transfers, a billing period — into a
   * tenant that may have signed up minutes ago, so a floor at `created_at` excludes almost
   * everything the samples exist to show.
   *
   * Measured on the seeded tenant, floored at its real `created_at` (8 days old):
   *
   *        transfers in window      2   of 22
   *        value delivered    $19,000   of $125,000
   *        resales_count            1   of 16
   *        median_days_to_first_scan  null (nothing in range to measure)
   *
   * So while samples are present the floor becomes `min(created_at, earliestEventAt)`.
   * Three things about that, all load-bearing:
   *
   *  - 🚨 IT IS DONE HERE, ON THE CLIENT, BECAUSE THE SERVER MUST NOT BACKDATE
   *    `companies.created_at`. That column is real, UNFENCED data — the brand's actual
   *    signup date, which drives their trial window — and `clear-intro-set` could not put
   *    it back, because nothing records what it was. The sample set never writes outside
   *    its fence; the correction belongs in the one place that is only a display window.
   *
   *  - IT CANNOT WIDEN A REAL BRAND'S WINDOW. `earliestEventAt` is `null` for a tenant
   *    with no samples, so `min` collapses to `created_at` and the behaviour is
   *    byte-identical to what shipped. And on a seeded tenant it cannot pull in real data
   *    either: anything dated before the company existed is necessarily a backdated
   *    sample row.
   *
   *  - THE `min` IS NOT REDUNDANT. `earliestEventAt` is not unconditionally older — a
   *    tenant that signed up two years ago and seeds samples today has a `created_at`
   *    well before the fabricated history, and taking the sample date alone would NARROW
   *    its window and drop its real invoices from the denominator.
   *
   * ── 🚩 AND THE COMPANY IS NOW THE AUTHENTICATED ONE, WHICH IT WAS NOT ─────────────
   *
   * This read was `useCompany({ id: DEFAULT_COMPANY_ID })` — a hardcoded uuid in
   * `config.ts` pointing at **Louis Vuitton**, on a card whose FIGURES come from the
   * authenticated company (the ROI controller ignores any client-sent `company_ids` and
   * uses `@CompanyId()`). So the window was floored at one tenant's signup date while
   * being measured over another's data.
   *
   * That was not merely untidy, it was ACTIVELY MASKING this fix. Louis Vuitton was
   * created 2025-10-02, which is older than the sample history's oldest event
   * (2025-10-11), so the accident happened to produce a wide-enough window and the
   * seeded tenant's hero looked correct. Leaving it would have meant a floor that is
   * right only for as long as one unrelated seed row keeps an early date — and wrong for
   * every real brand, whose "all time" would start on Louis Vuitton's birthday. Fixing it
   * is what makes this change verifiable at all: the stated scope moves from
   * "since Oct 2, 2025" (LV) to "since Oct 11, 2025" (the sample floor).
   *
   * `useCurrentUserCompany` is `GET /companies/admin/me` — already in this hook module,
   * already scoped by the auth context, no id to get wrong. The same hardcoded id is used
   * by ~10 other call sites in this app and is NOT swept here; that is a separate change
   * with its own blast radius, and this one only claims the site it depends on.
   */
  const { data: company, isLoading: companyLoading } = useCurrentUserCompany()

  /**
   * `isLoading`, not `isReady`: the intro-status query runs with `retry: false`, so an
   * ERROR settles it without ever reaching success. Gating on `isReady` would pin this
   * card to a skeleton forever on a failed status call — the same degradation the
   * `companyLoading` note below rejects. On error `earliestEventAt` stays `undefined`,
   * the floor falls back to `created_at`, and the card behaves exactly as it did before
   * this change.
   */
  const { hasIntroData, earliestEventAt, isLoading: introLoading } = useIntroData()

  /**
   * The floor: the company's own creation date, or the older of that and the sample
   * history's start while samples are present. See the long note above.
   *
   * Guarded on `hasIntroData === true` rather than on `earliestEventAt` being non-null.
   * They agree today, but the flag is the STATEMENT ("this tenant is showing samples")
   * and the date is derived from it; keying on the date alone would silently widen a real
   * brand's window the day anything else starts populating that field.
   */
  const dataFloor = useMemo(() => {
    const createdAt = company?.created_at ?? null
    if (!createdAt) return null
    if (hasIntroData !== true || !earliestEventAt) return createdAt
    return new Date(earliestEventAt) < new Date(createdAt) ? earliestEventAt : createdAt
  }, [company?.created_at, hasIntroData, earliestEventAt])

  const allTimeRange = useRoiHeroAllTimeRange(dataFloor)

  /**
   * The window this card is measured over: the resolved all-time range, or `null` while the
   * floor is still loading.
   *
   * ⚠️ THE `companyLoading` GATE IS NOT REDUNDANT WITH `allTimeRange` BEING NON-NULL, and
   * dropping it would reintroduce a visible double-take. With the floor unresolved the
   * shared factory does not fail — it falls back to `VERA_DATA_FLOOR` (2020-01-01) and
   * returns a perfectly valid range. So the card would render a 2020-based window, fetch
   * it, and then swap to the real one: two requests, the chart's whole bucket spine
   * redrawn from ~80 empty months to a handful of real ones, and — now that the scope is
   * PRINTED — the stated "since" date visibly changing under the reader. The figures
   * themselves would not move (there are no invoices before the company existed), which is
   * exactly what makes it read as a glitch rather than as data arriving.
   *
   * `isLoading`, not `!company`: a company request that ERRORS must not pin the card to a
   * skeleton forever. Settled-and-empty falls through to the floor fallback, which is the
   * degradation the `dataFloor` parameter documents.
   *
   * ⚠️ `introLoading` joins it for the SAME reason, and this one moves the FIGURES rather
   * than just the stated date. With the sample floor unresolved the range resolves to the
   * company's own `created_at`, which on a freshly-seeded tenant is a window holding 2 of
   * its 22 transfers — so the card would paint $19,000 and 1 resale, then swap to $125,000
   * and 16 when the status call lands. A headline multiple that visibly corrects itself is
   * worse than one that arrives late.
   */
  const activeRange = companyLoading || introLoading ? null : allTimeRange

  const { data: heroData, refetch: refetchHero } = useAnalyticsRoiHero(
    {
      // `activeRange` is null only while `enabled` is false, so these fallbacks are never
      // the values actually sent. They exist because the params object is built before the
      // gate is read.
      from: activeRange?.from ?? new Date(),
      to: activeRange?.to ?? new Date(),
      // ⚠️ NO `company_ids`. It used to send `[DEFAULT_COMPANY_ID]` — the hardcoded Louis
      // Vuitton uuid — which was never read: `RoiMetricsRequestQueryDto` has no such
      // field and `AnalyticsAdminController.getRoiMetrics` builds `companyIds` from
      // `@CompanyId()`, the auth context. Removing it changes no behaviour and stops the
      // request naming a tenant it has nothing to do with, next to a floor that is now
      // correctly the authenticated company's.
      certificate_ids: filters.certificateIds.length > 0 ? filters.certificateIds : undefined,
      owner_ids: filters.ownerIds.length > 0 ? filters.ownerIds : undefined,
      product_ids: filters.productIds.length > 0 ? filters.productIds : undefined,
      location: filters.location.length > 0 ? filters.location : undefined,
      // The collapsible chart's data. Requested unconditionally rather than on expand, so
      // opening the chart is instant and cannot show its own second skeleton — the series
      // is a handful of buckets on a response the card is already waiting for.
      value_series: true,
      // The DENOMINATOR over the same buckets, plus a per-bucket running multiple. Sent
      // together with `value_series` rather than relying on the server's implication —
      // the spend points reuse the value buckets and the running multiple divides the
      // cumulative value, so asking for one without the other is incoherent.
      spend_series: true,
    },
    { enabled: Boolean(activeRange) },
  )

  /**
   * ── THE HERO REFETCHES ITSELF WHEN THE BRAND'S FIGURES CHANGE ─────────────────────
   *
   * Saving a business figure moves the numerator, so `Partial · N of M sources`, Value
   * delivered and the multiple are all stale the instant a `needs_input` metric gets its
   * input. The ROI CARDS already refresh — `AnalyticsPage` owns their request and calls
   * `refetchRoi()` on save. This card was the one surface that did not, so a save moved a
   * card from an em-dash to a figure while the hero above it still read `2 of 5`, and only
   * a reload reconciled them. The server was right throughout; the client simply never
   * asked again.
   *
   * ⚠️ THE REFETCH HAS TO BE ISSUED FROM IN HERE, and that is the whole reason the bug
   * existed rather than an implementation preference. This card owns its own period (see
   * the header note) and issues its own request into its own slot. Nothing outside it
   * knows `activeRange`, so no external caller can dispatch on its behalf without
   * GUESSING a window — and a `GET_ANALYTICS_ROI_HERO` dispatched with the page's range
   * would write `analytics.roiHero` with a window this card is not displaying, which is
   * exactly the last-writer-wins failure the second slot exists to prevent.
   *
   * ✅ WHY THIS CANNOT RACE ANYTHING. `refetchHero` re-dispatches `GET_ANALYTICS_ROI_HERO`
   * with THIS component's current params, so:
   *   - it writes `analytics.roiHero` only. The cards' `refetchRoi()` writes
   *     `analytics.roi`. Two slots, two actions, two sagas — no shared writer, so the
   *     two refreshes cannot clobber each other however they interleave.
   *   - `getAnalyticsRoiHero` is dispatched from `useAnalyticsRoiHero`, which only this
   *     component calls. So the slot has exactly one writer, and its saga is `takeLatest`
   *     — if this refetch overlaps the params-change fetch, the earlier is cancelled and
   *     the survivor is still this card's window. There is no params combination under
   *     which the loser could paint a different range.
   *
   * The trigger is the metric-inputs QUERY CACHE, not a prop threaded down from
   * `AnalyticsPage`. `useSetMetricInputs` / `useRemoveMetricInput` write that cache on
   * every save and retraction, so every save site — the in-card popover today, anything
   * added later — notifies this card for free. A prop would have to be plumbed up from
   * the popover, through the cards, into the page, and back down, coupling a page to a
   * concern it does not have and covering only the save sites someone remembered to wire.
   *
   * `enabled: false`: this is a SUBSCRIPTION, not a read. The card never renders a figure
   * from these values and must not add a request to the analytics page's cold load; it
   * only needs to be told when someone else's copy changed.
   */
  const { data: metricInputs } = useMetricInputs({ enabled: false })

  /**
   * The saved figures reduced to a comparable string.
   *
   * By CONTENT, not by object identity: the mutations seed the cache with
   * `setQueryData`, so the object is new on every write including a write that changed
   * nothing (re-saving the same number, or a plain refetch). Sorted, because the server's
   * row order is not a contract and re-ordering is not a change. `null` while the cache is
   * empty, which is distinct from `''` for a company that has saved nothing.
   */
  const inputsFingerprint = metricInputs
    ? metricInputs.values
        .map(v => `${v.key}=${v.value}`)
        .sort()
        .join('|')
    : null

  /**
   * `undefined` = never observed. The FIRST value seen is recorded without refetching:
   * it arrives when some other surface first loads the catalogue, which is not a change
   * to anything and would otherwise cost a duplicate ROI request on every page where the
   * popover is opened.
   */
  const seenInputsRef = useRef<string | null | undefined>(undefined)

  /**
   * `useQuerySelect` rebuilds `refetch` on every render, so depending on it directly
   * would re-run this effect every render. The ref always holds the latest closure, and
   * that closure always carries this render's params.
   */
  const refetchHeroRef = useRef(refetchHero)
  refetchHeroRef.current = refetchHero

  const hasPeriod = Boolean(activeRange)

  useEffect(() => {
    const previous = seenInputsRef.current
    seenInputsRef.current = inputsFingerprint

    if (previous === undefined) return
    if (previous === inputsFingerprint) return
    // Without a resolved period the params carry `new Date()` placeholders, and fetching
    // that zero-length window would write the slot with a range nobody asked for.
    if (!hasPeriod) return

    refetchHeroRef.current()
  }, [inputsFingerprint, hasPeriod])

  const roi = heroData?.roi

  /**
   * `IRoiMetrics.reporting_currency` — the same request's `data` half, not `roi` (which
   * is `IRoiSummary` and carries no currency field of its own). A fact about the figures
   * below (which rows are in the aggregate), not a display preference — see
   * `analyticsMoney.ts`.
   */
  /*
    TWO CURRENCIES ON THIS ONE BLOCK, AND CONFLATING THEM IS NOT A ROUNDING ERROR.

    `currency` — the brand's declared reporting currency. Everything Vera DERIVED for
    them is in it: value delivered, the contributors, the value series. Money
    aggregates include only rows already priced in it (VERA-260).

    `spendCurrency` — the currency Stripe actually SETTLED Vera's invoices in, chosen
    from the invoice history by `selectSpendCurrency`. It is a fact about money that
    genuinely changed hands, and it is NOT the same field.

    They usually agree. When they do not, formatting a USD-settled invoice total with a
    `£` because the brand reports in GBP would restate one currency's amount as
    another's — the same silent relabelling that read-time FX conversion was rejected
    for, and the reason `METRIC_INPUT_CURRENCY` was likewise left un-wired. So spend is
    drawn in its OWN currency and value in the brand's, which also has the useful
    property of making a divergence VISIBLE to the reader rather than hiding it behind
    a uniform symbol.

    ⚠️ THE MULTIPLE ABOVE STILL DIVIDES ONE BY THE OTHER WITHOUT COMPARING THEM
    (`get-roi-metrics.use-case.ts` — `valueDeliveredCents / spend.total_cents`). That is
    a real defect and it is NOT fixed here: what the hero should show when the two
    disagree is a product decision, and blanking the page's headline figure as a side
    effect of a formatting change would be the wrong way to make it. Tracked on VRG-497.

    Uppercased because Stripe returns lowercase (`'usd'`), so the raw values would not
    even compare equal to a reporting currency that is CHECK-constrained to uppercase.
  */
  const currency = heroData?.data?.reporting_currency
  const spendCurrency =
    heroData?.roi?.spend?.currency?.toUpperCase() ?? currency

  /**
   * ── OPEN BY DEFAULT ────────────────────────────────────────────────────────────────
   *
   * It shipped collapsed, on the reasoning that the card's subject is a single 56px figure
   * and a chart under it is supporting detail. That inverts what the chart actually is: it
   * is the multiple's OWN EVIDENCE — the same numerator, resolved into the dated events
   * that produced it — sitting directly beneath the number it explains. A reader who
   * doubts "3.2×" is exactly the reader who should see the lumps, and a collapsed panel is
   * a panel most people never open.
   *
   * It stays COLLAPSIBLE, which is the part that makes opening it safe: a reader who wants
   * the headline and nothing else can shut it, and the card is then no taller than it was.
   * The disclosure is a way out of the detail, not a gate in front of it.
   */
  const [chartOpen, setChartOpen] = useState(true)

  /** Whether there is a series to disclose at all — see the trigger for why. */
  const hasSeries = Boolean(roi?.value_series && roi.value_series.length > 0)

  /**
   * ── THE SKELETON IS FOR THE FIRST LOAD ONLY (§19) ──────────────────────────────────
   *
   * `!roi` rather than `isLoading` alone, twice over. `useQuerySelect` flips `isLoading`
   * on every params change, so a dimension-filter change would otherwise swap the loaded
   * hero for a grey block and back — the "all cards flash" report, and the "page moves up
   * a little" one, since the skeleton is a different height from a hero carrying a caveat.
   * And `enabled: false` sets `isLoading` FALSE, so while the company's `created_at` — and
   * therefore the all-time floor — is still in flight, `isLoading` says "settled" when
   * nothing has arrived.
   *
   * So the test is the honest one: has a response ever landed for this card?
   */
  if (!roi) {
    // Footprint parity (§19): the skeleton occupies the same band inside the same card the
    // loaded hero will — figures LEFT, the two controls top-RIGHT on the same band, which
    // is the loaded layout rather than the stacked one it replaced. It mirrors the loaded
    // hero's ASYMMETRY too — one tall bar then two shorter ones — rather than three equal
    // columns, so the skeleton is not itself a layout shift.
    return (
      <Card sx={{ p: 6 }}>
        <Stack
          direction="row"
          spacing={6}
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={6} sx={{ flex: '1 1 520px', minWidth: 0 }}>
            <Stack spacing={3} sx={{ flex: 1 }}>
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton variant="text" width={160} height={HERO_MULTIPLE_STAT_SIZE + 12} />
            </Stack>
            {[0, 1].map(i => (
              <Stack key={i} spacing={3} sx={{ flex: 1 }}>
                <Skeleton variant="text" width={140} height={20} />
                <Skeleton variant="text" width={180} height={HERO_INPUT_STAT_SIZE + 12} />
              </Stack>
            ))}
          </Stack>
          {/* TWO TEXT-HEIGHT BARS, not two 40px pills. Both controls in this band are now
              borderless text triggers (`DisclosureTextButton` and the `emphasis="text"`
              period select), and a skeleton reserving 40px for each would be 22px taller
              than what replaces it — footprint parity (§19) means matching the loaded
              height, not the height the controls used to have. */}
          <Stack direction="row" spacing={3} alignItems="center" sx={{ flexShrink: 0, height: 40 }}>
            <Skeleton variant="text" width={120} height={18} />
            <Skeleton variant="text" width={140} height={18} />
          </Stack>
        </Stack>
      </Card>
    )
  }

  const { value_delivered_cents: delivered, spend, multiple, withheld_metric_ids: withheld } = roi

  /**
   * The same withheld metrics WITH their reason and missing inputs, which is what the
   * sources tooltip is composed from.
   *
   * `?? []` rather than trusting the field, on the same reasoning as `typeof === 'number'`
   * on the multiple: this component is pinned to every view on the page, so a response
   * shape that drifts must degrade the tooltip rather than throw inside `.filter` during
   * render and bounce the reader out through the global ErrorBoundary. An older payload
   * carrying `withheld_metric_ids` but not `withheld` then yields the scope sentence alone,
   * which is a smaller loss than the page.
   */
  const withheldDetail = roi.withheld ?? []

  // The caveat's two numbers, named once. `countable` is what the numerator COULD count
  // today, not the whole metric registry — a metric that is blocked outright (no
  // instrumentation) is not "waiting on you" and would make the ratio read worse than the
  // situation is.
  const counted = roi.contributing_metric_ids.length
  const countable = counted + withheld.length

  /**
   * ── VRG-506: THE ROWS DROPPED *INSIDE* A CONTRIBUTING METRIC ──────────────────────
   *
   * `Partial · N of M sources` accounts for whole metrics that could not be computed. It
   * says nothing about rows dropped WITHIN one that could, and there are two such filters
   * feeding Value delivered — both of them deliberate, both of them silent until now:
   *
   *   - `resale_value_excluded_currency` — completed resales priced in a currency other
   *     than the reporting one. Money is never FX-converted on this page (a figure
   *     converted at read time drifts as rates move and stops matching the screenshot a
   *     brand took of it), so a foreign-priced resale is excluded rather than restated.
   *   - `stolen_recovery_unpriced` — recovered certificates whose product carries no
   *     price, so there is no amount to add.
   *
   * Both interface fields say IN THEIR OWN DOCSTRINGS that they exist to be reported —
   * "a value metric that silently drops rows understates the total and leaves a reader
   * unable to tell a real figure from a filtered one" — and until now neither had a
   * reader anywhere in the client. The field was computed, returned, and dropped on the
   * floor, which is precisely the failure it was added to prevent.
   *
   * ## Both, not just the currency one
   *
   * VRG-506 was filed about the currency exclusion alone and flagged the unpriced one as
   * "check for the same defect". It has it. Surfacing one and not the other would be its
   * own small lie: a reader told the figure omits foreign-priced resales reasonably infers
   * that is the only thing omitted.
   *
   * ## Gated on the metric actually CONTRIBUTING
   *
   * A withheld metric is already excluded WHOLESALE and counted in `N of M`, so its
   * dropped rows understate nothing that is on screen — saying "4 resales are not
   * included" about a metric none of whose resales are included would double-count the
   * caveat and misdescribe it. `contributing_metric_ids` carries exactly the ids that made
   * it into the sum (`ROI_CONTRIBUTION_RESOLVERS`), so each clause is gated on its own.
   */
  /*
    ⚠️ BOTH COUNTS COME FROM `heroData.data`, NOT FROM `roi` — the same two-halves split
    `currency` above documents. `roi` is `IRoiSummary` (one numerator, one division) and
    carries no per-metric detail; the counts live on `IRoiMetrics`, the `data` half of the
    very same response, so reading them costs no extra request.
  */
  const contributing = new Set(roi.contributing_metric_ids)
  const excludedResaleValue = contributing.has('resale_value_captured')
    ? (heroData?.data?.resale_value_excluded_currency ?? 0)
    : 0
  const unpricedRecovery = contributing.has('stolen_recovery_value')
    ? (heroData?.data?.stolen_recovery_unpriced ?? 0)
    : 0

  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

  /*
    ⚠️ SECONDARY TONE, NOT AMBER, AND NOT A VISIBLE LINE.

    Two decisions, and both follow rules already settled on this page rather than being
    made here:

    1. TONE. This is a FACT about which rows are in the aggregate, not a warning about the
       arithmetic — `resale_value_excluded_currency`'s docstring makes the distinction
       explicitly ("the number it accompanies is correct"). `AnalyticsBlock`'s `note` is
       the secondary-toned treatment for exactly that register, the same one
       `resales_unpriced` and `transfer_completion_unstamped` already use.

    2. PLACEMENT. It goes in Value delivered's OWN `(i)`, not in the caveat row below the
       figures. That row is the home of the JOINT qualifiers — this file's header says the
       three figures share one card precisely "because the footnotes below qualify all
       three" — and this qualifies exactly one of them. Spend is untouched by it, so a line
       down there would claim a constraint on the denominator that does not exist.

       The precedent is one card away and identical in shape: `useRoiCards` puts the very
       same sentence about the very same rows ("priced in a different currency and are not
       included") in the average-resale-price tile's `(i)`, recording it as "per the epic's
       tooltip decision, it renders in the ⓘ rather than as a permanent amber sentence on
       the card face". Same fact, same register, same placement.

    The two rejected candidates, for the record. Extending `Partial · N of M sources` would
    corrupt what that line counts: it is whole metrics, and resale value IS one of the N —
    the exclusion happens inside it. A third entry in the caveat row is option 2 above.

    `note` composes with the existing `term="value-delivered"`: `InfoTooltip` renders the
    caveat and still footers through to the glossary definition, and the `(i)` is marked
    (`caveatMarkSx`) while one is live, so the icon itself signals there is something to
    read. And if the figure were ever an em-dash, `AnalyticsBlock` moves the note inline of
    its own accord (`showCaveatInline`) — nothing to special-case here.
  */
  const valueDeliveredNote =
    [
      excludedResaleValue > 0
        ? `${excludedResaleValue} completed ${plural(excludedResaleValue, 'resale is', 'resales are')} priced in a currency other than ${currency} and ${plural(excludedResaleValue, 'is', 'are')} not included. Prices are never converted, so a total mixing currencies would not mean anything.`
        : null,
      unpricedRecovery > 0
        ? `${unpricedRecovery} recovered ${plural(unpricedRecovery, 'certificate carries', 'certificates carry')} no product price, so ${plural(unpricedRecovery, 'it adds', 'they add')} nothing to this total.`
        : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  /**
   * ── THE SPEND CAVEAT, NOW TWO VARIANTS OF ONE LINE ────────────────────────────────
   *
   * An earlier pass DELETED an always-on AMBER "Spend approximate for this range" line,
   * because under the old plan-projection the caveat fired on nearly every load,
   * including on the common FALSE-POSITIVE case (a brand that never changed plan is
   * priced correctly by today's rate anyway). A caveat that is usually wrong teaches a
   * reader that this page's warnings are noise, which costs the one beside it that is
   * not.
   *
   * A LATER pass made the firing condition ALWAYS-ON instead: settled invoices alone
   * are short of whatever has accrued in the OPEN, not-yet-invoiced period, and every
   * range ending at `now` — including the `all_time` default — reaches into it. So the
   * warning fired on essentially every load, honestly this time (the denominator really
   * was short), but that is still the shape a reader learns to stop reading.
   *
   * ⚠️ THIS IS THE FIX FOR THAT, not a reversion. The API (`vera-spend-invoice.source.ts`)
   * now reads the open period's accrual LIVE from Stripe and, when it can confirm it,
   * folds it into `spend.total_cents` — the gap the warning existed to name is gone in
   * the common case. Two outcomes follow from `spend.exact`:
   *
   *   - `exact: false` — a REAL gap remains (Stripe unreachable, no subscription to
   *     preview, an excluded currency, a truncated invoice walk). `spendGapWarning`
   *     renders the same "Spend incomplete · return may be overstated" line as before.
   *   - `exact: true` with `spend.accrued_cents > 0` — the accrual WAS confirmed and is
   *     already inside the figure above. `spendAccruedInfo` renders a DIFFERENT,
   *     purely informational line — not a warning, because nothing is wrong — so the
   *     reader knows the number is current rather than silently missing something.
   *
   * Both stay `text.secondary`, one clamped line, full sentence on hover — the same
   * treatment as the partial-sources caveat beside them. The informational variant is
   * NOT amber for the same reason the warning variant never was: loudness should track
   * whether something is actually wrong, not just whether a line is present.
   *
   * ⚠️ BOTH REQUIRE A MULTIPLE TO QUALIFY, for the same reason as before: the visible
   * lines are statements ABOUT a return ("may be overstated" / the total is current),
   * and with `multiple === null` there is no return on screen for either to qualify —
   * it would render directly above the line explaining that none could be computed.
   *
   * `spendGapWarning` is worst under `source: 'unavailable'`, where `exact` is false and
   * the note is set, so it fires while the denominator is UNKNOWN rather than merely
   * incomplete. "Overstated" asserts a direction, and a direction cannot be known from a
   * measurement that failed. `NO_SPEND_EXPLANATION` carries that case instead, and says
   * the zero is not a reading.
   */
  const spendGapWarning =
    typeof multiple === 'number' && !spend.exact && Boolean(spend.approximation_note)

  const spendAccruedInfo =
    typeof multiple === 'number' && spend.exact && spend.accrued_cents > 0

  return (
    <Card sx={{ p: 6 }}>
      {/*
        ── THE CARD'S HEADER BAND: FIGURES LEFT, CONTROLS TOP-RIGHT ──────────────────────

        The two controls used to sit in a row of their OWN above the figures, which cost 56px
        (a 40px control plus its 16px gap) of card height before the first number and pushed
        the headline down for chrome. They are now pinned to the card's top-right corner on
        the same band as the figures' title row, so the figures start at the top of the card's
        content box — measured: each figure's top moved up by 56px and the card lost 112px
        overall, the second 56 coming from the disclosure trigger that used to sit BELOW the
        figures and has joined this group.

        This is the arrangement every other card on the page already uses — `AnalyticsCardShell`
        puts its title left and its controls right on one band — so the hero stops being the
        one card whose controls own a dedicated row.

        ⚠️ It supersedes the previous note that a control "inside the row would attach itself
        to whichever figure it sat next to". That objection was about a control placed BETWEEN
        figures, sharing their rhythm. These sit outside the figure group entirely, hard against
        the card's right edge — the canonical home for card-level chrome, which is what reads
        them as scoping the card rather than the nearest column. The joint caveats stay BELOW
        the figures for the original reason, unchanged: they qualify the numbers, not the card.
      */}
      <Stack
        direction="row"
        spacing={6}
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        {/* The answer, then the two operands that produced it.

            NOT three equal thirds. The multiple takes `flex: 1` on its own and the input
            PAIR shares `flex: 2`, so each input lands at roughly a quarter of the group
            against the multiple's half — the horizontal weight matching the type weight
            instead of arguing with it.

            The rule between them is the grouping cue: without it, three figures at two sizes
            read as one uneven row rather than as "headline, then its inputs". It is hidden
            below `md`, where the columns have wrapped and a vertical rule would be pointing
            at nothing. */}
        <Stack
          direction="row"
          spacing={6}
          flexWrap="wrap"
          useFlexGap
          alignItems="flex-start"
          // `1 1 520px` — the three figures' own minimums plus their gaps. Below that the
          // whole group wraps ABOVE the controls rather than compressing a 56px figure,
          // which is the one thing on this card that must not shrink.
          sx={{ flex: '1 1 520px', minWidth: 0 }}
        >
        <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
          {/* NULL, never Infinity. A brand in trial or on a comped plan has no
              denominator, and an infinite ROI would be the single most embarrassing
              number on this page. */}
          {/*
            `typeof === 'number'`, NOT `!== null`. The narrower check is a one-character
            difference that decides whether a bad payload degrades or takes the page down:
            `multiple` is typed `number | null`, so an `undefined` — from a response shape
            drifting, or from reading the wrong key off the ROI response — passes a
            `!== null` guard and reaches `.toFixed`, which throws DURING RENDER and bounces
            the user out through the global ErrorBoundary.

            That is not hypothetical. The identical pattern in `useRoiCards` did exactly
            that during this epic (`.roi` vs `.data` — the hero wants one, the cards the
            other), and the resulting "Something went wrong" was misattributed to Home,
            which was merely the redirect TARGET. This component is pinned to every view,
            so it is the worst place in the page to carry that failure mode.

            Leading the hero makes this MORE load-bearing, not less: the figure that throws
            is now the first thing the page tries to render.
          */}
          {/*
            `term`, and this is the tile that most needs one (§44/§59). The single most
            consequential fact about this figure — that it is measured over ALL TIME and
            therefore does NOT follow the page's date range — is invisible on its face, and
            the card's `All time · since <date>` line states the scope without explaining
            why it differs from everything below it. The definition carries the reason (the
            denominator is settled invoices, so a window between two of them divides by
            zero), plus the dimension-filter asymmetry: a filter narrows the numerator while
            the denominator stays company-wide.

            The three tiles' terms are authored here rather than derived from `title` —
            "Return on Vera" would de-space to `return-on-vera` by luck, "Spend with Vera"
            would not resolve at all, and a `term` that misses renders NO tooltip rather
            than a broken one.
          */}
          <AnalyticsBlock
            surface="bare"
            statSize={HERO_MULTIPLE_STAT_SIZE}
            title="Return on Vera"
            term="return-on-vera"
            stat={typeof multiple === 'number' ? `${multiple.toFixed(1)}×` : '—'}
          />
        </Box>

        <Stack
          direction="row"
          spacing={6}
          flexWrap="wrap"
          useFlexGap
          sx={{
            flex: '2 1 340px',
            pl: { md: 6 },
            borderLeft: { xs: 'none', md: `1px solid ${theme.palette.primary[100]}` },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <AnalyticsBlock
              surface="bare"
              statSize={HERO_INPUT_STAT_SIZE}
              title="Value delivered"
              // The definition carries what the figure cannot: that an unmeasurable metric
              // is left OUT of the sum rather than counted as zero, so the total understates
              // rather than flatters. That is the question a brand asks of a number smaller
              // than they expected, and the footnote below answers only half of it.
              term="value-delivered"
              // Currency is a fact about the figure (which rows are in the aggregate),
              // not a display preference — see `analyticsMoney.ts`.
              stat={formatMoneyCentsCompact(delivered, currency)}
              // VRG-506 — the rows dropped INSIDE a contributing metric, which the joint
              // caveat row below cannot speak for. See `valueDeliveredNote`. `undefined`
              // when nothing was dropped, so the `(i)` is unmarked on the clean case.
              note={valueDeliveredNote}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 160 }}>
            <AnalyticsBlock
              surface="bare"
              statSize={HERO_INPUT_STAT_SIZE}
              title="Spend with Vera"
              stat={formatMoneyCentsCompact(spend.total_cents, spendCurrency)}
              // `term` ALONGSIDE the breakdown below, not instead of it — the two compose.
              // `InfoTooltip` takes `text` as the body and still footers through to the
              // definition, so this tile keeps its live per-company numbers on hover AND
              // gains a route to what the figure IS. That combination is the case
              // `AnalyticsBlock.term`'s own doc calls the useful one: a card whose local
              // phrasing is genuinely local (these are this company's actual charges) but
              // whose subject is still a term of art.
              term="vera-spend"
              // The breakdown was already a hover on this figure; it is now the shared tile's
              // own info affordance rather than a second hand-placed icon + Tooltip pair.
              tooltip={
                <Box>
                  <Typography variant="caption" display="block">
                    Subscription {formatMoneyCentsCompact(spend.subscription_cents, spendCurrency)} + activations{' '}
                    {formatMoneyCentsCompact(spend.activation_cents, spendCurrency)}
                    {/* `accrued_cents` is ADDITIVE to `total_cents` — see IVeraSpend — so it
                        is named here too, or the three numbers above would not reconcile
                        with the tile's own headline figure. */}
                    {spend.accrued_cents > 0 && (
                      <> + accrued {formatMoneyCentsCompact(spend.accrued_cents, spendCurrency)}</>
                    )}
                  </Typography>
                  {spend.activations_consumed > 0 && (
                    <Typography variant="caption" display="block" mt={0.5}>
                      {spend.activations_billable} of {spend.activations_consumed} activations
                      billable; the rest were included in your plan.
                    </Typography>
                  )}
                  {spend.approximation_note && (
                    // NOT always `warning.main`. Once accrued usage is successfully
                    // confirmed, `approximation_note` carries an INFORMATIONAL sentence
                    // rather than a warning — `exact` is what tells the two apart (see
                    // IVeraSpend.approximation_note's two-case doc). Colouring an FYI
                    // amber would say something is wrong when nothing is.
                    <Typography
                      variant="caption"
                      display="block"
                      mt={1}
                      color={spend.exact ? 'text.secondary' : 'warning.main'}
                    >
                      {spend.approximation_note}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
        </Stack>
        </Stack>

        {/*
          ── THE CARD'S CONTROLS ───────────────────────────────────────────────────────

          🚨 THIS GROUP HELD TWO CONTROLS AND NOW HOLDS ONE. The `Period:` select that sat
          to the right of the disclosure is gone; the card's scope is fixed at all time and
          is STATED in the caveat row below instead (`formatRoiHeroScope`). See
          `RoiHeroPeriod` for why every option that select offered made the figure
          worse-founded than the default it sat beside, and why removing it is only honest
          because the page's date picker moved down into the toolbar in the same change.

          The Stack survives the removal deliberately rather than collapsing to a bare
          button: the disclosure is still card-level chrome that belongs hard against the
          right edge, and the wrap/`ml: 'auto'` behaviour below is what keeps it there when
          the figures push it onto a line of its own. A lone child in a flex row costs
          nothing, and the next control added here inherits the arrangement.

          `flexShrink: 0` so the trigger never compresses — it is a text control whose whole
          job is to be readable.
        */}
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          // `ml: 'auto'` is a no-op on the unwrapped row (`space-between` already puts the
          // group at the right edge) and is what keeps it there once the figures push it
          // onto a line of its own. The wrap threshold moved OUT with the period select —
          // the group is ~130px now rather than ~490px, so it holds its corner far longer —
          // but the rule is unchanged: without this, a wrapped group jumps to the LEFT edge
          // and the control appears to change corner with the viewport.
          sx={{ flexShrink: 0, ml: 'auto' }}
        >
          {/*
            ── THE DISCLOSURE TRIGGER LIVES HERE NOW, NOT AT THE CARD'S BOTTOM EDGE ──────

            It used to sit immediately above the panel it opens, on the reasoning that a
            control at the far end of a card from what it reveals makes the reader hunt for
            what changed. That reasoning applied to a panel that was CLOSED by default and
            whose trigger was the only thing on screen referring to it.

            Open by default, the trade reverses: the chart is already visible, so the
            trigger is a "collapse this" control rather than a "find out what this is" one,
            and a collapse control belongs with the card's other chrome. Grouping the two
            controls is also what frees the 56px the figures moved up into — the trigger was
            the only thing between the figures and the caveat line.

            ⚠️ IT IS NO LONGER A BORDERED `Button`, AND THE INFO ICON THAT SAT BESIDE IT
            HAS MOVED. As `variant="secondary"` this was a 40px outlined control at the same
            weight as the period select, so "look at the chart" competed visually with the
            one choice that changes every figure on the card. It is a disclosure, not an
            action, and it now renders as the same low-emphasis text trigger the billing
            page's plan-comparison matrix uses for expand/collapse-all — extracted to
            `DisclosureTextButton` on its second use (§36) rather than copied.

            The chevron convention comes with it: DOWN when the region is showing, rotated
            to point RIGHT when it is hidden. That inverts this card's previous "point up
            when open" reading, deliberately, because it is the convention every accordion
            in the app already uses and two conventions in one product actively mislead.

            The scope tooltip that used to sit here now hangs off `Partial · N of M
            sources` below. Beside this button it explained the chart; on the sources line
            it explains the caveat, which is the question a reader actually has.
          */}
          {hasSeries && (
            <DisclosureTextButton
              expanded={chartOpen}
              onToggle={() => setChartOpen(open => !open)}
              controls="roi-hero-value-chart"
              // THE LABEL NAMES THE ACTION, NOT THE CONTENT. It read "Value delivered over
              // time" — the panel's subject — which is the one thing a disclosure trigger
              // should not say: with the panel already open (the default), a button
              // labelled with what is visible below it does not read as a control at all.
              //
              // BOTH strings, not `chartOpen ? … : …`. Resolved here, the button's width
              // tracked its own state (the two labels differ by 7.15px), and because this
              // control group sits opposite the figures in a `space-between` row whose
              // figure group is `flex: 1 1 520px`, that group absorbed the difference and
              // walked the figures sideways — 2.39px for "Value delivered", 4.78px for
              // "Spend with Vera" — on a control that is only supposed to change height.
              // Handing over the pair lets the button reserve the wider one.
              label={{ expanded: 'Collapse chart', collapsed: 'Expand chart' }}
            />
          )}
        </Stack>
      </Stack>

      {/*
        ── THE SCOPE AND THE CAVEATS ─────────────────────────────────────────────────────

        All of them qualify all three figures JOINTLY, which is why they sit outside the
        figure row rather than on a tile: attached to any one tile each would be read as
        being about that tile alone.

        🚨 THE SCOPE LINE IS NEW AND IT IS THE HALF THAT REPLACES A CONTROL. The card used to
        say what it was measured over through a `Period:` select in its top-right corner; the
        select is gone (`RoiHeroPeriod`) and the window is fixed at all time, so the card has
        to STATE its scope in its own copy or stop saying it at all — and a return figure that
        does not say what it covers is exactly the number a reader mis-reads.

        ## Why it is a stated line and NOT a disabled-looking control in the old slot

        A static `Period: All time` left where the trigger was reads as a control that has
        stopped working — the one thing worse than a picker offering bad options is chrome
        that looks interactive and is not (§46 makes the same call for actions). Stating it in
        the qualifier row instead puts it with the other things that constrain the figures,
        in the same weight, which is what it actually is.

        ## Why HERE and not up beside the figures

        This row is already the home of every joint qualifier. Scope is the most fundamental
        of them — "over what window" precedes "how complete is the numerator" and "how
        complete is the denominator" — so it goes FIRST in the row and the caveats follow.

        ## Why it renders UNCONDITIONALLY

        The other two entries are conditional, and the row used to render only when one of
        them fired. Scope is not conditional: it is true on every load, including the common
        clean one where neither caveat applies, and a card whose stated window appears only
        when something is also wrong with it would teach a reader that its absence means
        "everything" rather than "we did not say". So the row's own gate is gone — it now
        always renders, with at least this line in it.

        `formatRoiHeroScope` falls back to the bare "All time" while the floor is loading, so
        the claim is never absent even for the one render where its date is not yet known.

        ⚠️ NO `title` attribute on a Tooltip child. MUI warns, loudly and per instance, when
        a Tooltip's child also carries `title`; the Tooltip already supplies the accessible
        description.
      */}
      {/*
        `Partial · N of M sources` qualifies the NUMERATOR, and it stays VISIBLE rather
        than living in a tooltip. It qualifies the multiple directly above it, and the
        multiple is the number a brand forms an opinion from. "Return on Vera 0.7×" reads
        as "this product is not paying for itself" — a conclusion someone acts on. If the
        true figure is materially higher because three of five contributing sources are not
        computable yet, and the only signal is behind a hover, the brand reaches the wrong
        conclusion about the product from the product's own dashboard and never learns
        otherwise. A tooltip is not read by someone who has already believed the number.
      */}
        <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
          {/* SCOPE FIRST — see the block above for why it leads and why it is unconditional.
              No tooltip: it is a complete statement, not an abbreviation of one. */}
          <Typography variant="caption" color="text.secondary" sx={CAVEAT_SX}>
            {formatRoiHeroScope(activeRange)}
          </Typography>

          {withheld.length > 0 && (
            /*
              THE VISIBLE LINE STAYS SHORT AND THE TOOLTIP CARRIES THE DETAIL, which is the
              split this page's locked decisions require: an assumption is stated INLINE and
              a tooltip is not read by someone who has already believed the number. So the
              line itself says the one thing that must survive being skimmed — the figure is
              partial — and everything about WHICH sources and WHY sits behind it.

              `N of M` is derived from `contributing_metric_ids` / `withheld_metric_ids`, so
              it stays honest on its own as metrics become computable, with no copy change.

              ⚠️ THE ICON THAT NOW EXPLAINS THIS USED TO SIT BESIDE THE CHART DISCLOSURE,
              where it explained the chart. `Partial · N of M sources` is the line that
              actually raises the question ("why 2 of 5?"), so the explanation belongs on it,
              and the composed text answers the follow-up the old copy never did ("what would
              make it 5?"). See `sourcesExplanation`.
            */
            <Tooltip
              arrow
              placement="top"
              title={sourcesExplanation(ROI_VALUE_CHART_SCOPE, withheldDetail)}
              // WIDER AND LOOSER THAN THE DEFAULT, because this one carries an
              // explanation rather than a label. MUI's 300px cap wrapped it to twelve
              // lines of roughly forty characters, which is below the readable band for
              // running prose and turned three sentences into a block. 360px lands lines
              // at ~55–60 characters; `lineHeight: 1.5` is the same breathing room §15
              // requires of modal body copy, for the same reason.
              slotProps={{ tooltip: { sx: { maxWidth: 360, lineHeight: 1.5 } } }}
            >
              <Typography variant="caption" color="text.secondary" sx={CAVEAT_SX}>
                Partial · {counted} of {countable} sources
              </Typography>
            </Tooltip>
          )}

          {spendGapWarning && (
            <Tooltip arrow placement="top" title={spend.approximation_note}>
              {/*
                The visible line states the DIRECTION, and does not name a cause.

                `exact: false` now has FOUR possible causes (Stripe unreachable, no
                subscription to preview the open period's accrual against, a currency
                excluded from the sum, a truncated invoice walk) and all four understate
                spend, so the one thing a reader can act on is common to them: the return
                above is a ceiling. The API's own `approximation_note` names the actual
                cause on hover, which is where a cause belongs when four of them share one
                line.
              */}
              <Typography variant="caption" color="text.secondary" sx={CAVEAT_SX}>
                Spend incomplete · return may be overstated
              </Typography>
            </Tooltip>
          )}

          {spendAccruedInfo && (
            <Tooltip arrow placement="top" title={spend.approximation_note}>
              {/*
                NOT a warning — see `spendAccruedInfo`'s doc above. The open period's
                accrual WAS confirmed and is already folded into the figures above; this
                line exists only so the reader knows the total is current rather than
                wondering why it moves between visits within one billing period.
              */}
              <Typography variant="caption" color="text.secondary" sx={CAVEAT_SX}>
                Includes {formatMoneyCentsCompact(spend.accrued_cents, spendCurrency)} accrued this period
              </Typography>
            </Tooltip>
          )}
        </Stack>

      {multiple === null && spend.total_cents === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, mt: 1, display: 'block' }}>
          {NO_SPEND_EXPLANATION[spend.source]}
        </Typography>
      )}

      {/*
        The disclosed panel. Its TRIGGER is up in the card's top-right control group — see
        that block for why it moved there, and `chartOpen` for why this opens expanded.

        Rendered only once there is a series to show, which is the same gate the trigger
        uses (`hasSeries`). A disclosure that opens onto an empty panel is worse than no
        disclosure; see `RoiValueChart` for the case where the series exists but is all
        zeros, which IS shown (no value delivered is a fact, not a gap).
      */}
      {roi.value_series && roi.value_series.length > 0 && (
        // `Collapse`, not a height transition of our own (§28/§47) — and it also settles
        // correctly where the animation clock never advances, because it drives its state
        // machine on a timer and sets `height: auto` on `entered`.
        <Collapse in={chartOpen} unmountOnExit>
          <Box id="roi-hero-value-chart" sx={{ mt: 5 }}>
            <RoiValueChart
              points={roi.value_series}
              spendPoints={roi.spend_series}
              // The DENOMINATOR'S PROVENANCE, so the chart can say why the return line has
              // no points rather than leaving the row silently absent. See
              // `ROI_UNAVAILABLE_REASON` — the three live causes are not interchangeable.
              spendSource={spend.source}
              granularity={roi.value_series_granularity}
              rangeLabel={formatRoiHeroRange(activeRange)}
              currency={currency}
            />
          </Box>
        </Collapse>
      )}
    </Card>
  )
}
